/**
 * compositionCensus.js — E3 (historia 152, §8.8)
 *
 * El **censo de composición** de HU-03: qué operaciones de la biblioteca están ya en el workflow
 * que se está editando, y cuántas veces (CA3.1, CA3.4, CA3.5). Módulo **puro**, sin React y sin
 * servidor, hermano de `assemblyHighlight.js` y `processPaletteTree.js`.
 *
 * Tres decisiones que este fichero encarna y que ningún otro puede desdecir:
 *
 * 1. **No hay endpoint de censo** (§7.1, decisión nº 5). Un endpoint contestaría sobre lo
 *    PERSISTIDO y mentiría en cuanto hubiera un cambio pendiente; CA3.4 y CA3.5 exigen que
 *    arrastrar, repetir y eliminar muevan el indicador **en el acto**. Por eso el censo se calcula
 *    aquí, sobre el **modelo vivo del editor**.
 * 2. **El censo es DERIVADO** (`IN13`, impacto R8). No escribe en el reducer, no se cachea en el
 *    modelo y no viaja en el `PUT`: recibe el workflow y devuelve un objeto nuevo, sin tocar ni un
 *    nodo. Es la misma advertencia que `processTreeHydration.js:9-14` dejó escrita para #118
 *    ("writing … would travel in the `PUT` as a user change"), y el motivo por el que la
 *    hidratación de los subworkflows **devuelve** el árbol en vez de escribirlo.
 * 3. **La identidad del censo es el VÍNCULO, nunca el origen** (`IN15`, `RN-03.6`, D11-bis). La
 *    única clave es el **código de operación** de los nodos con vínculo **vivo**
 *    (`SYNCHRONIZED`/`CUSTOMIZED`), sea cual sea la versión: una versión de biblioteca es inmutable
 *    y no se re-identifica nunca. En este fichero **no aparece `sourceProcessCode`**: proceso de
 *    origen y censo son independientes y un cambio en uno no puede alterar el otro (S91).
 *
 * Quedan FUERA del recuento (`RN-03.3b`): los nodos `UNLINKED` —y los que tienen una
 * desvinculación PENDIENTE—, los embebidos aunque su contenido sea idéntico, las copias locales
 * (`intent: 'COPY'`, que nacen embebidas) y los nodos WORKFLOW, que son ramas y no operaciones.
 *
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').Workflow} Workflow
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').Node} Node
 * @typedef {Object} CompositionCensus
 * @property {Map<string, number>} presentByOperationCode Recuento de NODOS por código de operación
 * @property {boolean} partial Alguna rama del workflow no se ha podido leer (`RN-03.4c`, S79)
 * @property {function(string): number} countOf Cuántos nodos vinculados a esa operación hay
 * @property {function(Object): boolean} isPresent Si una fila del árbol de un proceso ya está
 */
import { OPERATION_LINK_STATE, resolveOperationLinkState } from './operationLinkIntegrity'

/** La intención pendiente que ROMPE el vínculo antes de guardar (§8.3): el nodo deja de contar. */
const UNBIND_INTENT = 'UNBIND'

/** Los dos estados con vínculo vivo — el único criterio de presencia que existe (`RN-03.3`). */
const LIVE_STATES = [OPERATION_LINK_STATE.SYNCHRONIZED, OPERATION_LINK_STATE.CUSTOMIZED]

const isSetNode = (node) => node?.nodeType === 'WORKFLOW'

/**
 * El código de operación con el que ESTE nodo aporta presencia, o `''` si no aporta ninguna.
 *
 * El estado lo resuelve `resolveOperationLinkState` —la misma función que pinta la cabecera del
 * nodo (C7)—, de modo que el censo y el panel no pueden discrepar. Lo único que se le añade es la
 * intención pendiente: desvincular es un cambio del editor y el censo incluye los cambios
 * pendientes (`RN-03.2`, S76).
 *
 * @param {Node} node
 * @returns {string} El código de operación, o `''`
 */
const liveOperationCodeOf = (node) => {
  if (!node || isSetNode(node)) {
    return ''
  }
  const operationLink = node.operationLink
  if (!operationLink || operationLink.intent === UNBIND_INTENT) {
    return ''
  }
  const resolved = resolveOperationLinkState(operationLink)
  return LIVE_STATES.includes(resolved.state) ? resolved.code || operationLink.code || '' : ''
}

/**
 * El corazón del censo: recorre los nodos —raíz y subworkflows, **a cualquier profundidad**
 * (`RN-03.2`, D9)— y cuenta un voto por cada nodo con vínculo vivo.
 *
 * `partial` declara que la cifra se ha calculado sobre lo LEÍDO: una rama que no cargó (la
 * hidratación la reporta por `nodeId`) o un subworkflow todavía sin hidratar no aportan y no se
 * pueden dar por vacíos (S79, `RN-03.4c`).
 *
 * **No recibe ni lee dato de origen alguno** (`IN15`): su entrada son los nodos y su salida, el
 * recuento.
 *
 * @param {Node[]} nodes
 * @param {string[]} [failedSetNodeIds] Ramas cuya carga falló (`processTreeHydration.js`)
 * @returns {{ counts: Map<string, number>, partial: boolean }}
 */
const countLiveOperationLinks = (nodes = [], failedSetNodeIds = []) => {
  const counts = new Map()
  const failed = new Set(failedSetNodeIds || [])
  let partial = false
  const walk = (level) => {
    (Array.isArray(level) ? level : []).forEach((node) => {
      if (isSetNode(node)) {
        const children = node?.subWorkflow?.nodes
        if (!Array.isArray(children) || failed.has(node.id)) {
          // Una rama no leída no se puede afirmar vacía: la cifra se declara parcial y la UI lo dice.
          partial = true
          return
        }
        walk(children)
        return
      }
      const operationCode = liveOperationCodeOf(node)
      if (!operationCode) {
        return
      }
      counts.set(operationCode, (counts.get(operationCode) || 0) + 1)
    })
  }
  walk(nodes)
  return { counts, partial }
}

/**
 * El censo del workflow en edición (CA3.1, S73, S79).
 *
 * @param {Workflow} [workflow] El modelo VIVO del editor, con los subworkflows ya hidratados
 * @param {Object} [options]
 * @param {string[]} [options.failedSetNodeIds] Las ramas que la hidratación no pudo cargar
 * @returns {CompositionCensus}
 */
const buildCompositionCensus = (workflow = {}, { failedSetNodeIds = [] } = {}) => {
  const { counts, partial } = countLiveOperationLinks(workflow?.nodes, failedSetNodeIds)
  const countOf = (operationCode) => counts.get(operationCode) || 0
  return {
    presentByOperationCode: counts,
    partial,
    countOf,
    /**
     * `RN-03.3`/`RN-03.3b`: una fila del árbol de un proceso está presente **si y solo si** es un
     * JOB con vínculo (`countsInCensus`) y su operación está en el censo. Un JOB local nunca lo
     * está: no es parte del censo, ni del numerador ni del denominador (S75).
     */
    isPresent: (row) => row?.countsInCensus === true && countOf(row?.operationCode || '') > 0
  }
}

/**
 * La pregunta que hace el filtro "pendientes de añadir" (`RN-03.8`, S82) sobre una operación de la
 * sección OPERATIONS, que no es una fila de árbol sino un código suelto.
 *
 * @param {CompositionCensus} census
 * @param {string} operationCode
 * @returns {boolean}
 */
const censusIncludesOperation = (census, operationCode) =>
  !!operationCode && (census?.countOf ? census.countOf(operationCode) : 0) > 0

export {
  buildCompositionCensus,
  censusIncludesOperation,
  countLiveOperationLinks
}
